import * as THREE from 'three';
import { RWR_DISPLAY } from '../core/SimConfig.js';

const TWO_PI = Math.PI * 2;

/**
 * Cockpit RWR scope — the visual half of the radar warning receiver (the audio
 * half lives in AudioManager). Symbology is rasterised to an offscreen canvas,
 * the static artwork (rings + tick marks) is composited over it, and the result
 * is mapped onto the `_RWR` plane in the HD cockpit.
 *
 * The scope is a nose-up PPI: the aircraft sits at the centre, straight up is
 * the nose, and each contact is placed at its true relative bearing and slant
 * range out to `RWR_DISPLAY.maxRange` — i.e. an in-cockpit alternative to the
 * GameHUD target diamonds rather than a signal-strength picture like the real
 * ALR-69.
 *
 * Symbology (see RWR_DISPLAY for the knobs):
 *   diamond + type code   every live enemy inside range
 *   larger diamond        the primary threat (highest `rwrPriority`, nearest wins ties)
 *   semicircle on top     the newest contact — the emitter acquired most recently
 *   ring around diamond   that emitter has a missile in the air
 *   circle + "M"          an inbound missile, at its own bearing and range
 *
 * Cost is bounded two ways: nothing is drawn outside the cockpit view, and the
 * canvas redraws at `refreshRate` rather than per frame. Contact records are
 * pooled, so a steady state allocates nothing.
 */
export default class RWRDisplay {
  constructor({ aircraft, model, cameraManager, enemyManager, enemyMissileSystem, assets }) {
    this.aircraft = aircraft;
    this.cameraManager = cameraManager;
    this.enemyManager = enemyManager;
    this.enemyMissileSystem = enemyMissileSystem;

    const cfg = RWR_DISPLAY;

    this.mesh = model.getObjectByName(cfg.node) || null;
    if (!this.mesh) {
      console.warn(`RWRDisplay: "${cfg.node}" mesh not found in model`);
      return;
    }

    // ── Offscreen canvas + screen material ───────────────────────────────
    const res = cfg.canvasResolution;
    this.canvas = document.createElement('canvas');
    this.canvas.width = res;
    this.canvas.height = res;
    this.ctx = this.canvas.getContext('2d');

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.flipY = cfg.flipY;
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.generateMipmaps = false;

    this._originalMaterial = this.mesh.material;
    this.material = new THREE.MeshBasicMaterial({
      map: this.texture,
      toneMapped: cfg.toneMapped,
      color: new THREE.Color().setScalar(cfg.screenBrightness),
    });
    this.mesh.material = this.material;
    // `_RWR` is a scene root in the GLB, not a child of `_Cockpit_HD`, so the
    // camera-mode LOD swap doesn't hide it — the scope owns its own visibility.
    this._originalVisible = this.mesh.visible;
    this.mesh.visible = false;

    // ── Static artwork, pre-tinted once ──────────────────────────────────
    // The PNG is authored in a single flat colour, so recolouring it is a
    // source-in fill rather than a per-pixel pass. `overlayColor: null` keeps
    // the artwork exactly as drawn.
    this._overlay = this._buildOverlay(assets?.getTexture(cfg.overlayTexture)?.image, res);

    // ── Scope state ──────────────────────────────────────────────────────
    this._time = 0;
    this._refreshAccum = Infinity;   // draw on the first cockpit frame
    this._powered = false;
    this._blanked = false;           // screen already cleared for an unpowered redraw

    // Acquisition order — entity → monotonically increasing sequence number.
    // The highest sequence still in contact is the "newest threat"; entries are
    // dropped as soon as a contact leaves the scope, so a re-entry reads as new.
    this._acquired = new Map();
    this._acqSeq = 0;
    this._seen = new Set();

    // Pooled contact records, refilled each redraw.
    this._contacts = [];
    this._contactCount = 0;
  }

  // ── Per-frame update ───────────────────────────────────────────────────

  update(dt = 0) {
    if (!this.mesh) return;

    const inCockpit = this._isCockpitView();
    this.mesh.visible = inCockpit;
    if (!inCockpit) {
      // Force a fresh draw on re-entry rather than showing a stale frame.
      this._refreshAccum = Infinity;
      return;
    }

    this._time += dt;

    const powered = this._avionicsPowered();
    if (powered !== this._powered) {
      this._powered = powered;
      this._refreshAccum = Infinity;
    }
    if (!powered && this._blanked) return;

    const interval = RWR_DISPLAY.refreshRate > 0 ? 1 / RWR_DISPLAY.refreshRate : 0;
    this._refreshAccum += dt;
    if (this._refreshAccum < interval) return;
    this._refreshAccum = 0;

    this._draw();
  }

  dispose() {
    if (!this.mesh) return;
    this.material?.dispose();
    this.texture?.dispose();
    // The GLB is shared across scenarios — hand the mesh back as we found it.
    if (this._originalMaterial) this.mesh.material = this._originalMaterial;
    this.mesh.visible = this._originalVisible;
    this._acquired.clear();
  }

  // ── Drawing ────────────────────────────────────────────────────────────

  _draw() {
    const cfg = RWR_DISPLAY;
    const ctx = this.ctx;
    const res = this.canvas.width;

    ctx.globalAlpha = 1;
    ctx.fillStyle = cfg.backgroundColor;
    ctx.fillRect(0, 0, res, res);

    if (!this._powered) {
      this._blanked = true;
      this.texture.needsUpdate = true;
      return;
    }
    this._blanked = false;

    this._collectContacts();
    this._drawContacts();

    // Artwork last, so rings and ticks read over the symbology.
    if (this._overlay) {
      ctx.globalAlpha = cfg.overlayAlpha;
      ctx.drawImage(this._overlay, 0, 0, res, res);
      ctx.globalAlpha = 1;
    }

    this.texture.needsUpdate = true;
  }

  /**
   * Rebuild the contact list: every live enemy plus every inbound missile
   * inside `maxRange`, resolved to scope pixels. Sorted by threat priority so
   * the `maxSymbols` cap sheds the least interesting marks, and so the primary
   * threat is simply the first emitter in the list.
   */
  _collectContacts() {
    const cfg = RWR_DISPLAY;
    const res = this.canvas.width;
    const half = res * 0.5;
    const maxRange = cfg.maxRange;

    // Nose-up rotation. Relative bearing is derived from the world delta with
    // the sin/cos angle-difference identities, so there's no atan2 per contact:
    //   sin(rel) ∝  dx·cosH + dz·sinH
    //   cos(rel) ∝ -dz·cosH + dx·sinH
    const fwd = this.aircraft.forwardVector;
    const heading = Math.atan2(fwd.x, -fwd.z);
    const cosH = Math.cos(heading);
    const sinH = Math.sin(heading);

    const p = this.aircraft.position;
    const inner = cfg.centerHole * half;
    const span = (cfg.outerRadius - cfg.centerHole) * half;

    this._contactCount = 0;
    this._seen.clear();

    const entities = this.enemyManager?.entities;
    if (entities) {
      for (let i = 0; i < entities.length; i++) {
        const e = entities[i];
        if (!e || e.dead || e.destroyed) continue;
        const pos = e.flight?.position;
        if (!pos) continue;

        const c = this._place(pos, p, cosH, sinH, half, inner, span, maxRange);
        if (!c) continue;

        const params = e.params;
        c.missile = false;
        c.code = params?.rwrCode ?? cfg.defaultCode;
        c.priority = params?.rwrPriority ?? 0;
        c.launching = false;
        c.entity = e;

        this._seen.add(e);
        let seq = this._acquired.get(e);
        if (seq === undefined) {
          seq = this._acqSeq++;
          this._acquired.set(e, seq);
        }
        c.seq = seq;
      }
    }

    // Inbound missiles. Each one also rings its launcher, so the shot reads
    // both as a track on the scope and as an armed emitter.
    const missiles = this.enemyMissileSystem?.activeMissiles;
    if (missiles) {
      for (let i = 0; i < missiles.length; i++) {
        const m = missiles[i];
        const src = m.source;
        if (src) {
          for (let j = 0; j < this._contactCount; j++) {
            if (this._contacts[j].entity === src) { this._contacts[j].launching = true; break; }
          }
        }
        const c = this._place(m.position, p, cosH, sinH, half, inner, span, maxRange);
        if (!c) continue;
        c.missile = true;
        c.code = 'M';
        // Above every emitter — an inbound round outranks whatever fired it.
        c.priority = Infinity;
        c.launching = false;
        c.entity = null;
        c.seq = -1;
      }
    }

    // Drop acquisitions for anything no longer on the scope.
    if (this._acquired.size > this._seen.size) {
      for (const e of this._acquired.keys()) {
        if (!this._seen.has(e)) this._acquired.delete(e);
      }
    }

    // Most dangerous first (priority, then nearest), so the head of the list is
    // the primary threat and the tail is what the symbol cap discards.
    const list = this._contacts;
    const n = this._contactCount;
    for (let i = 1; i < n; i++) {
      const item = list[i];
      let j = i - 1;
      while (j >= 0 && (list[j].priority < item.priority
        || (list[j].priority === item.priority && list[j].dist > item.dist))) {
        list[j + 1] = list[j];
        j--;
      }
      list[j + 1] = item;
    }

    if (this._contactCount > cfg.maxSymbols) this._contactCount = cfg.maxSymbols;
  }

  /**
   * Resolve a world position to a pooled contact record in scope pixels, or
   * null when it's out of range. Radius uses slant range (matching the GameHUD
   * readout); bearing is horizontal only, as on a real azimuth-only scope.
   */
  _place(pos, p, cosH, sinH, half, inner, span, maxRange) {
    const dx = pos.x - p.x;
    const dy = pos.y - p.y;
    const dz = pos.z - p.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dist > maxRange) return null;

    const flat = Math.hypot(dx, dz);
    // Directly overhead/below has no bearing — park it on the nose.
    let ux = 0, uy = 1;
    if (flat > 1e-3) {
      ux = (dx * cosH + dz * sinH) / flat;
      uy = (-dz * cosH + dx * sinH) / flat;
    }

    const t = maxRange > 0 ? dist / maxRange : 0;
    const r = inner + span * Math.pow(t, RWR_DISPLAY.rangeExponent);

    const c = this._contacts[this._contactCount] || (this._contacts[this._contactCount] = {});
    this._contactCount++;
    c.x = half + ux * r;
    c.y = half - uy * r;   // canvas y grows downward; the nose is up
    c.dist = dist;
    return c;
  }

  _drawContacts() {
    const cfg = RWR_DISPLAY;
    const ctx = this.ctx;
    const n = this._contactCount;
    if (n === 0) return;

    // The newest emitter still in contact carries the acquisition cap.
    let newest = null;
    let newestSeq = -1;
    for (let i = 0; i < n; i++) {
      const c = this._contacts[i];
      if (!c.missile && c.seq > newestSeq) { newestSeq = c.seq; newest = c; }
    }
    // Sorted most-dangerous-first, so the first emitter is the primary threat.
    let primary = null;
    for (let i = 0; i < n; i++) {
      if (!this._contacts[i].missile) { primary = this._contacts[i]; break; }
    }

    const blinkOn = cfg.blinkPeriod > 0
      ? (this._time % (cfg.blinkPeriod * 2)) < cfg.blinkPeriod
      : true;

    ctx.lineWidth = cfg.lineWidth;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < n; i++) {
      const c = this._contacts[i];

      if (c.missile) {
        if (!blinkOn) continue;
        ctx.strokeStyle = cfg.missileColor;
        ctx.fillStyle = cfg.missileColor;
        ctx.beginPath();
        ctx.arc(c.x, c.y, cfg.missileRadius, 0, TWO_PI);
        ctx.stroke();
        ctx.font = `${cfg.missileFontSize}px ${cfg.font}`;
        ctx.fillText('M', c.x, c.y + 1);
        continue;
      }

      ctx.strokeStyle = cfg.color;
      ctx.fillStyle = cfg.color;

      const s = cfg.symbolSize * (c === primary ? cfg.primaryScale : 1);

      ctx.beginPath();
      ctx.moveTo(c.x, c.y - s);
      ctx.lineTo(c.x + s, c.y);
      ctx.lineTo(c.x, c.y + s);
      ctx.lineTo(c.x - s, c.y);
      ctx.closePath();
      ctx.stroke();

      // Newest contact — semicircle capping the top two faces of the diamond.
      if (c === newest) {
        ctx.beginPath();
        ctx.arc(c.x, c.y, s * cfg.newestArcScale, Math.PI, TWO_PI);
        ctx.stroke();
      }

      // Missile away from this emitter — full ring around the diamond.
      if (c.launching && blinkOn) {
        ctx.beginPath();
        ctx.arc(c.x, c.y, s * cfg.launchRingScale, 0, TWO_PI);
        ctx.stroke();
      }

      ctx.font = `${cfg.fontSize * (c === primary ? cfg.primaryScale : 1)}px ${cfg.font}`;
      ctx.fillText(c.code, c.x, c.y + 1);
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  /**
   * Flat-tint the artwork into a canvas once at construction. Returns the
   * source image untouched when `overlayColor` is null, or null when the
   * texture never loaded.
   */
  _buildOverlay(image, res) {
    if (!image) {
      console.warn(`RWRDisplay: overlay texture "${RWR_DISPLAY.overlayTexture}" not loaded`);
      return null;
    }
    const tint = RWR_DISPLAY.overlayColor;
    if (!tint) return image;

    const c = document.createElement('canvas');
    c.width = res;
    c.height = res;
    const ctx = c.getContext('2d');
    ctx.drawImage(image, 0, 0, res, res);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = tint;
    ctx.fillRect(0, 0, res, res);
    return c;
  }

  _isCockpitView() {
    return !!this.cameraManager?.cockpitView;
  }

  /** Avionics power — same engine-driven crossover the cockpit HUD uses. */
  _avionicsPowered() {
    const eng = this.aircraft?.engineState;
    if (!eng) return false;
    if (eng.isStarting) return eng.transitionProgress >= 0.5;
    if (eng.isStopping) return eng.transitionProgress < 0.5;
    return eng.isRunning;
  }
}
